import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Pencil, Check, X } from 'lucide-react';

const DAYS = [
  { key: 'lun', label: 'Lun' },
  { key: 'mar', label: 'Mar' },
  { key: 'mie', label: 'Mié' },
  { key: 'jue', label: 'Jue' },
  { key: 'vie', label: 'Vie' },
  { key: 'sab', label: 'Sáb' },
  { key: 'dom', label: 'Dom' },
];

// ──────────────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────────────

function MonitorChip({ name, color }) {
  return (
    <span
      className="inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 leading-tight whitespace-nowrap"
      style={{ background: color + '1A', color }}
    >
      {name}
    </span>
  );
}

function CellEdit({ value, onSave, onCancel }) {
  const [monitors, setMonitors] = useState(value.monitors.join(', '));
  const [note, setNote]         = useState(value.note || '');

  const handleKey = (e) => {
    if (e.key === 'Enter') onSave(monitors, note);
    if (e.key === 'Escape') onCancel();
  };

  return (
    <div className="space-y-1.5 min-w-[140px]" onClick={e => e.stopPropagation()}>
      <input
        autoFocus
        value={monitors}
        onChange={e => setMonitors(e.target.value)}
        onKeyDown={handleKey}
        placeholder="Monitor1, Monitor2"
        className="w-full text-[11px] border border-[#48293A]/40 rounded-lg px-2 py-1 text-center focus:outline-none focus:ring-1 focus:ring-[#48293A]/50"
      />
      <input
        value={note}
        onChange={e => setNote(e.target.value)}
        onKeyDown={handleKey}
        placeholder="Nota (opcional)"
        className="w-full text-[10px] border border-black/15 rounded-lg px-2 py-1 text-center text-[#4C4D57] focus:outline-none focus:ring-1 focus:ring-black/20"
      />
      <div className="flex gap-1 justify-center pt-0.5">
        <button
          onClick={() => onSave(monitors, note)}
          className="flex items-center gap-1 text-[10px] font-bold bg-[#27AE60] text-white rounded-lg px-2.5 py-1 hover:bg-[#229954]"
        >
          <Check size={10} /> OK
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-1 text-[10px] bg-black/8 text-[#4C4D57] rounded-lg px-2 py-1 hover:bg-black/12"
        >
          <X size={10} />
        </button>
      </div>
    </div>
  );
}

function TimeSlotEdit({ value, onSave, onCancel }) {
  const [slot, setSlot] = useState(value);
  return (
    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
      <input
        autoFocus
        value={slot}
        onChange={e => setSlot(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') onSave(slot); if (e.key === 'Escape') onCancel(); }}
        className="text-[10px] border border-[#48293A]/40 rounded px-1.5 py-0.5 w-full focus:outline-none focus:ring-1 focus:ring-[#48293A]/50"
      />
      <button onClick={() => onSave(slot)} className="text-[#27AE60] flex-shrink-0"><Check size={11} /></button>
      <button onClick={onCancel} className="text-[#4C4D57] flex-shrink-0"><X size={11} /></button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────────────

export default function ScheduleTable({ editable = false }) {
  const { api } = useAuth();
  const [schedule, setSchedule] = useState([]);
  const [loading,  setLoading]  = useState(true);

  // Editing state
  const [editCell,     setEditCell]     = useState(null); // { activityId, day }
  const [editSlotId,   setEditSlotId]   = useState(null); // activityId

  const fetchSchedule = useCallback(async () => {
    try {
      const { data } = await api.get('/monitor-schedule');
      setSchedule(data);
    } catch {
      toast.error('Error al cargar el cuadrante');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  const saveCell = async (activityId, day, monitorsStr, note) => {
    const monitors = monitorsStr.split(',').map(m => m.trim()).filter(Boolean);
    try {
      await api.patch(`/admin/monitor-schedule/${activityId}`, {
        days: { [day]: { monitors, note: note || null } },
      });
      await fetchSchedule();
      setEditCell(null);
      toast.success('Cuadrante actualizado');
    } catch {
      toast.error('Error al guardar');
    }
  };

  const saveSlot = async (activityId, time_slot) => {
    try {
      await api.patch(`/admin/monitor-schedule/${activityId}`, { time_slot });
      await fetchSchedule();
      setEditSlotId(null);
      toast.success('Horario actualizado');
    } catch {
      toast.error('Error al guardar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-[#48293A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-black/8 shadow-sm bg-white">
      <table
        className="border-collapse"
        style={{ minWidth: '720px', width: '100%', tableLayout: 'fixed' }}
      >
        {/* ── Column widths ────────────────────────────── */}
        <colgroup>
          <col style={{ width: '172px' }} />
          {DAYS.map(d => <col key={d.key} style={{ width: '90px' }} />)}
        </colgroup>

        {/* ── Header ───────────────────────────────────── */}
        <thead>
          <tr>
            <th
              className="sticky left-0 z-20 bg-[#48293A] text-left px-4 py-3 text-white font-semibold text-xs uppercase tracking-wider"
              style={{ borderRight: '1px solid rgba(255,255,255,0.12)' }}
            >
              Actividad
            </th>
            {DAYS.map(d => (
              <th
                key={d.key}
                className="bg-[#48293A] text-center text-white font-semibold text-xs uppercase tracking-wider py-3 px-1"
                style={{ borderLeft: '1px solid rgba(255,255,255,0.08)' }}
              >
                {d.label}
              </th>
            ))}
          </tr>
        </thead>

        {/* ── Body ─────────────────────────────────────── */}
        <tbody>
          {schedule.map((act, rowIdx) => {
            const rowBg = rowIdx % 2 === 0 ? '#FFFFFF' : '#FAFAF8';

            return (
              <tr key={act.id}>
                {/* Activity name — sticky */}
                <td
                  className="sticky left-0 z-10 px-3 py-3 align-top"
                  style={{
                    background: rowBg,
                    borderRight: '1px solid rgba(0,0,0,0.07)',
                    borderTop: rowIdx > 0 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                    borderLeft: `3px solid ${act.color}`,
                  }}
                >
                  <div className="font-bold text-[12px] text-[#111111] leading-tight">
                    {act.activity}
                  </div>

                  {editable && editSlotId === act.id ? (
                    <div className="mt-1">
                      <TimeSlotEdit
                        value={act.time_slot}
                        onSave={v => saveSlot(act.id, v)}
                        onCancel={() => setEditSlotId(null)}
                      />
                    </div>
                  ) : (
                    <div
                      className="text-[10px] text-[#4C4D57] mt-0.5 leading-tight"
                      style={{ cursor: editable ? 'pointer' : 'default' }}
                      onClick={editable ? () => setEditSlotId(act.id) : undefined}
                      title={editable ? 'Editar horario' : undefined}
                    >
                      {act.time_slot}
                      {editable && (
                        <Pencil
                          size={8}
                          className="inline ml-1 opacity-30 hover:opacity-70"
                        />
                      )}
                    </div>
                  )}
                </td>

                {/* Day cells */}
                {DAYS.map(d => {
                  const cell       = act.days?.[d.key] || {};
                  const monitors   = cell.monitors || [];
                  const note       = cell.note || null;
                  const isEditing  = editCell?.activityId === act.id && editCell?.day === d.key;

                  return (
                    <td
                      key={d.key}
                      className="py-2.5 px-1.5 align-top text-center"
                      style={{
                        background: rowBg,
                        borderLeft: '1px solid rgba(0,0,0,0.05)',
                        borderTop: rowIdx > 0 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                        cursor: editable && !isEditing ? 'pointer' : 'default',
                      }}
                      onClick={
                        editable && !isEditing
                          ? () => {
                              setEditCell({ activityId: act.id, day: d.key });
                              setEditSlotId(null);
                            }
                          : undefined
                      }
                      title={editable && !isEditing ? 'Clic para editar' : undefined}
                    >
                      {isEditing ? (
                        <CellEdit
                          value={{ monitors, note }}
                          onSave={(m, n) => saveCell(act.id, d.key, m, n)}
                          onCancel={() => setEditCell(null)}
                        />
                      ) : (
                        <div className="space-y-0.5">
                          {monitors.length === 0 ? (
                            <span className="text-[#8C8C8C] text-[11px]">—</span>
                          ) : (
                            monitors.map(m => (
                              <div key={m} className="flex justify-center">
                                <MonitorChip name={m} color={act.color} />
                              </div>
                            ))
                          )}
                          {note && (
                            <p className="text-[9px] text-[#4C4D57] italic mt-1 leading-tight px-1">
                              {note}
                            </p>
                          )}
                          {editable && (
                            <div className="opacity-0 group-hover:opacity-100 pt-0.5">
                              <Pencil size={9} className="text-[#8C8C8C] mx-auto" />
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
